// Theme/Skin Configuration System
import { doc, getDoc, getDocFromServer, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface ThemeConfig {
  id: string;
  name: string;
  description: string;
  fonts: {
    display: string;
    body: string;
    googleFontsUrl: string;
  };
  colors: {
    background: string;
    foreground: string;
    card: string;
    cardForeground: string;
    popover: string;
    popoverForeground: string;
    primary: string;
    primaryForeground: string;
    secondary: string;
    secondaryForeground: string;
    muted: string;
    mutedForeground: string;
    accent: string;
    accentForeground: string;
    destructive: string;
    destructiveForeground: string;
    border: string;
    input: string;
    ring: string;
    // Cosmic/theme specific
    cosmicDeep: string;
    cosmicNebula: string;
    cosmicStar: string;
    cosmicMoon: string;
    cosmicSun: string;
    cosmicGlow: string;
    // Month colors
    months: string[];
    dayOutOfTime: string;
  };
}

export const THEMES: Record<string, ThemeConfig> = {
  dreamspell: {
    id: 'dreamspell',
    name: 'Dreamspell Teal',
    description: 'Teal dusk and gilded celestial accents inspired by the Dreamspell calendar',
    fonts: {
      display: "'Cinzel Decorative', 'Cinzel', serif",
      body: "'Cormorant Garamond', 'Georgia', serif",
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700&family=Cinzel:wght@400;500;600;700&family=Cormorant+Garamond:wght@300;400;500;600;700&display=swap',
    },
    colors: {
      background: '190 45% 8%',
      foreground: '45 35% 92%',
      card: '188 38% 12%',
      cardForeground: '45 30% 90%',
      popover: '188 40% 10%',
      popoverForeground: '45 35% 92%',
      primary: '42 80% 60%',
      primaryForeground: '190 30% 10%',
      secondary: '190 30% 20%',
      secondaryForeground: '45 35% 88%',
      muted: '190 25% 16%',
      mutedForeground: '45 20% 70%',
      accent: '45 85% 55%',
      accentForeground: '190 30% 12%',
      destructive: '0 70% 50%',
      destructiveForeground: '45 30% 92%',
      border: '190 25% 22%',
      input: '190 25% 18%',
      ring: '45 85% 55%',
      cosmicDeep: '190 45% 6%',
      cosmicNebula: '185 40% 20%',
      cosmicStar: '45 80% 75%',
      cosmicMoon: '46 25% 85%',
      cosmicSun: '42 90% 60%',
      cosmicGlow: '45 85% 55%',
      months: [
        '42 75% 62%',
        '34 70% 58%',
        '25 65% 55%',
        '15 60% 52%',
        '350 45% 55%',
        '200 45% 50%',
        '185 50% 48%',
        '170 55% 45%',
        '155 60% 42%',
        '140 65% 40%',
        '120 60% 38%',
        '100 55% 40%',
        '60 70% 58%',
      ],
      dayOutOfTime: '45 90% 65%',
    },
  },
  tech: {
    id: 'tech',
    name: 'Cosmic Tech',
    description: 'Futuristic space-age aesthetic with neon glows and digital precision',
    fonts: {
      display: "'Orbitron', system-ui, sans-serif",
      body: "'Space Grotesk', system-ui, sans-serif",
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700&family=Space+Grotesk:wght@300;400;500;600;700&display=swap',
    },
    colors: {
      background: '230 25% 7%',
      foreground: '210 40% 96%',
      card: '230 30% 10%',
      cardForeground: '210 40% 96%',
      popover: '230 30% 10%',
      popoverForeground: '210 40% 96%',
      primary: '270 70% 60%',
      primaryForeground: '210 40% 98%',
      secondary: '220 60% 20%',
      secondaryForeground: '210 40% 96%',
      muted: '230 20% 15%',
      mutedForeground: '220 15% 55%',
      accent: '180 70% 45%',
      accentForeground: '210 40% 98%',
      destructive: '0 70% 50%',
      destructiveForeground: '210 40% 98%',
      border: '230 25% 18%',
      input: '230 25% 15%',
      ring: '270 70% 60%',
      cosmicDeep: '230 35% 5%',
      cosmicNebula: '270 60% 50%',
      cosmicStar: '45 100% 75%',
      cosmicMoon: '220 30% 85%',
      cosmicSun: '35 100% 60%',
      cosmicGlow: '270 70% 60%',
      months: [
        '0 70% 55%',
        '28 80% 55%',
        '45 90% 55%',
        '75 65% 50%',
        '120 55% 45%',
        '160 60% 45%',
        '180 65% 45%',
        '200 70% 50%',
        '230 65% 55%',
        '260 60% 55%',
        '290 55% 55%',
        '320 60% 55%',
        '270 70% 60%',
      ],
      dayOutOfTime: '45 100% 70%',
    },
  },
  mayan: {
    id: 'mayan',
    name: 'Ancient Maya',
    description: 'Sacred geometry and jungle temple aesthetics inspired by Mayan civilization',
    fonts: {
      display: "'Cinzel Decorative', 'Cinzel', serif",
      body: "'Cormorant Garamond', 'Georgia', serif",
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700&family=Cinzel:wght@400;500;600;700&family=Cormorant+Garamond:wght@300;400;500;600;700&display=swap',
    },
    colors: {
      // Deep jungle and stone temple tones
      background: '35 30% 8%',
      foreground: '40 25% 88%',
      card: '30 25% 12%',
      cardForeground: '40 25% 88%',
      popover: '30 25% 12%',
      popoverForeground: '40 25% 88%',
      // Jade green primary
      primary: '160 65% 40%',
      primaryForeground: '40 30% 95%',
      // Terracotta secondary
      secondary: '20 50% 25%',
      secondaryForeground: '40 25% 88%',
      muted: '35 20% 18%',
      mutedForeground: '35 15% 50%',
      // Gold/amber accent
      accent: '45 85% 50%',
      accentForeground: '35 30% 10%',
      destructive: '0 65% 45%',
      destructiveForeground: '40 30% 95%',
      border: '30 20% 22%',
      input: '30 20% 18%',
      ring: '160 65% 40%',
      // Mayan cosmic theme
      cosmicDeep: '35 35% 5%',
      cosmicNebula: '160 50% 35%',
      cosmicStar: '45 90% 65%',
      cosmicMoon: '40 20% 80%',
      cosmicSun: '40 90% 55%',
      cosmicGlow: '160 65% 40%',
      // 13 months - inspired by Mayan colors: jade, obsidian, gold, blood red, turquoise
      months: [
        '0 60% 45%',      // Red (blood sacrifice)
        '25 75% 50%',     // Orange (fire)
        '45 85% 50%',     // Gold (sun)
        '80 50% 40%',     // Olive green (maize)
        '160 60% 38%',    // Jade green
        '175 55% 40%',    // Teal
        '190 60% 42%',    // Turquoise (sacred)
        '210 50% 45%',    // Deep blue (sky)
        '260 45% 45%',    // Purple (royalty)
        '320 50% 45%',    // Magenta
        '350 55% 45%',    // Crimson
        '15 65% 48%',     // Terracotta
        '40 80% 45%',     // Amber
      ],
      dayOutOfTime: '45 90% 60%',
    },
  },
};

const THEME_COLLECTION = 'uiPrefs';
const THEME_DOC_ID = 'theme';

const shouldDisableFirestore = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1';

const getThemeDocRef = () => {
  if (!db) throw new Error('Firestore is not configured.');
  return doc(db, THEME_COLLECTION, THEME_DOC_ID);
};

const logFirestoreWrite = (detail: Record<string, unknown>) => {
  try {
    console.info('[firestore:write] theme', detail);
  } catch {
    // ignore
  }
};

export const DEFAULT_THEME = 'dreamspell';

export function getTheme(themeId: string): ThemeConfig {
  return THEMES[themeId] || THEMES[DEFAULT_THEME];
}

export function applyTheme(theme: ThemeConfig): void {
  const root = document.documentElement;
  
  // Apply colors as CSS custom properties
  root.style.setProperty('--background', theme.colors.background);
  root.style.setProperty('--foreground', theme.colors.foreground);
  root.style.setProperty('--card', theme.colors.card);
  root.style.setProperty('--card-foreground', theme.colors.cardForeground);
  root.style.setProperty('--popover', theme.colors.popover);
  root.style.setProperty('--popover-foreground', theme.colors.popoverForeground);
  root.style.setProperty('--primary', theme.colors.primary);
  root.style.setProperty('--primary-foreground', theme.colors.primaryForeground);
  root.style.setProperty('--secondary', theme.colors.secondary);
  root.style.setProperty('--secondary-foreground', theme.colors.secondaryForeground);
  root.style.setProperty('--muted', theme.colors.muted);
  root.style.setProperty('--muted-foreground', theme.colors.mutedForeground);
  root.style.setProperty('--accent', theme.colors.accent);
  root.style.setProperty('--accent-foreground', theme.colors.accentForeground);
  root.style.setProperty('--destructive', theme.colors.destructive);
  root.style.setProperty('--destructive-foreground', theme.colors.destructiveForeground);
  root.style.setProperty('--border', theme.colors.border);
  root.style.setProperty('--input', theme.colors.input);
  root.style.setProperty('--ring', theme.colors.ring);
  
  // Cosmic/theme specific
  root.style.setProperty('--cosmic-deep', theme.colors.cosmicDeep);
  root.style.setProperty('--cosmic-nebula', theme.colors.cosmicNebula);
  root.style.setProperty('--cosmic-star', theme.colors.cosmicStar);
  root.style.setProperty('--cosmic-moon', theme.colors.cosmicMoon);
  root.style.setProperty('--cosmic-sun', theme.colors.cosmicSun);
  root.style.setProperty('--cosmic-glow', theme.colors.cosmicGlow);
  
  // Month colors
  theme.colors.months.forEach((color, index) => {
    root.style.setProperty(`--month-${index + 1}`, color);
  });
  root.style.setProperty('--day-out-of-time', theme.colors.dayOutOfTime);
  
  // Sidebar colors (derived from main colors)
  root.style.setProperty('--sidebar-background', theme.colors.card);
  root.style.setProperty('--sidebar-foreground', theme.colors.foreground);
  root.style.setProperty('--sidebar-primary', theme.colors.primary);
  root.style.setProperty('--sidebar-primary-foreground', theme.colors.primaryForeground);
  root.style.setProperty('--sidebar-accent', theme.colors.muted);
  root.style.setProperty('--sidebar-accent-foreground', theme.colors.foreground);
  root.style.setProperty('--sidebar-border', theme.colors.border);
  root.style.setProperty('--sidebar-ring', theme.colors.ring);
  
  // Apply fonts
  root.style.setProperty('--font-display', theme.fonts.display);
  root.style.setProperty('--font-body', theme.fonts.body);
  
  // Update font links in head
  const existingFontLink = document.getElementById('theme-fonts');
  if (existingFontLink) {
    existingFontLink.remove();
  }
  
  const fontLink = document.createElement('link');
  fontLink.id = 'theme-fonts';
  fontLink.rel = 'stylesheet';
  fontLink.href = theme.fonts.googleFontsUrl;
  document.head.appendChild(fontLink);
  
  // Apply body and heading font families
  document.body.style.fontFamily = theme.fonts.body;
  
  // Add data attribute for theme-specific CSS
  root.setAttribute('data-theme', theme.id);
}

export function loadSavedTheme(): string {
  try {
    const saved = localStorage.getItem('app-theme');
    if (saved && saved in THEMES) {
      return saved;
    }
    return DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function saveTheme(themeId: string): void {
  try {
    localStorage.setItem('app-theme', themeId);
  } catch {
    // ignore storage errors (e.g., privacy mode)
  }

  if (db && !shouldDisableFirestore()) {
    try {
      logFirestoreWrite({ action: 'saveTheme', themeId });
      void setDoc(getThemeDocRef(), { themeId }, { merge: true }).catch(() => {
        // Ignore denied/offline remote theme persistence and keep local theme state.
      });
    } catch {
      // ignore network errors
    }
  }
}

export async function loadThemeFromRemote(): Promise<string | null> {
  if (!db || shouldDisableFirestore()) return null;
  const ref = getThemeDocRef();
  try {
    const snapshot = await getDocFromServer(ref);
    if (snapshot.exists()) return (snapshot.data() as { themeId?: string }).themeId ?? null;
  } catch {
    const snapshot = await getDoc(ref);
    if (snapshot.exists()) return (snapshot.data() as { themeId?: string }).themeId ?? null;
  }
  return null;
}
